import {
    _NotificationsListDataFragment,
    GetActiveNotificationsDocument,
    GetArchivedNotificationsDocument,
    GetDraftNotificationsDocument,
    GetNotificationsArgsSort,
    GetScheduledNotificationsDocument,
    GetSentNotificationsDocument,
    useGetActiveNotificationsQuery,
    useGetArchivedNotificationsQuery,
    useGetDraftNotificationsQuery,
    useGetNotificationByIdQuery,
    useGetNotificationsAverageReceivedQuery,
    useGetNotificationsCountQuery,
    useGetNotificationsFiltersFieldsQuery,
    useGetScheduledNotificationsQuery,
    useGetSentNotificationsQuery,
    GetFeatureFlagsDocument,
    GetFeatureFlagsQuery
} from '../../../../generated/apollo-hooks';
import useSelector from '../../../infrastructure/useSelector';
import { shallowEqual } from 'react-redux';
import {
    CC_PERMISSIONS,
    FeatureFlagKeys,
    hasRequiredPermissions,
    ICCUser,
    INotification,
    PERM_TYPES,
    ROLES,
    TableKeys
} from '@communication-center/data-objects';
import IStore from '../../../infrastructure/store.interface';
import { Store } from 'redux';
import { useMemo, useState } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { TabsConfig } from '../../../models';
import { ITableData } from '../../AppTable/model';
import { useFeatureFlag, _mergeFeatureFlags } from '../../AppPreloading/hooks/settings/useUserSettings';

export const notificationsQueries = {
    [TableKeys.ACTIVE]: {
        useQuery: useGetActiveNotificationsQuery,
        doc: GetActiveNotificationsDocument,
        dataKey: TableKeys.ACTIVE
    },
    [TableKeys.SCHEDULED]: {
        useQuery: useGetScheduledNotificationsQuery,
        doc: GetScheduledNotificationsDocument,
        dataKey: TableKeys.SCHEDULED
    },
    [TableKeys.SENT]: {
        useQuery: useGetSentNotificationsQuery,
        doc: GetSentNotificationsDocument,
        dataKey: TableKeys.SENT
    },
    [TableKeys.DRAFTS]: {
        useQuery: useGetDraftNotificationsQuery,
        doc: GetDraftNotificationsDocument,
        dataKey: TableKeys.DRAFTS
    },
    [TableKeys.ARCHIVED]: {
        useQuery: useGetArchivedNotificationsQuery,
        doc: GetArchivedNotificationsDocument,
        dataKey: TableKeys.ARCHIVED
    }
};

const useSkip = () => {
    const onlyUserNotifications = useOnlyUserNotificationsVariable();

    const { environmentId, systemId, authorized, rehydrated } = useSelector(
        state => ({
            environmentId: state.common?.currentEnvironment?.id,
            systemId: state.common?.currentSystem,
            authorized: state.common.authorized,
            rehydrated: state._persist?.rehydrated
        }),
        shallowEqual
    );

    return !authorized || !systemId || !environmentId || !rehydrated || onlyUserNotifications === undefined;
};

export const getOnlyUserNotificationsVariable = ({ getState }: { getState: Store<IStore>['getState'] }, apolloClient: ApolloClient<$TSFixMe>) => {
    const user = getState().common.user;
    const allNotificationsView = getState().notifications.allNotificationsView;
    const featureFlagsData = apolloClient.readQuery<GetFeatureFlagsQuery>({
        query: GetFeatureFlagsDocument,
        variables: { systemId: getState().common.currentSystem }
    });
    const _featureFlags = featureFlagsData?.getUserSettings?.featureFlags;
    const overrideFeatureFlags = getState().common.overrideFeatureFlags;
    const featureFlags = _mergeFeatureFlags(_featureFlags, overrideFeatureFlags);
    const permissionsFF = featureFlags[FeatureFlagKeys.Permissions];
    if (allNotificationsView === undefined) {
        if (!permissionsFF) {
            return user.role?.displayName !== ROLES.ADMIN;
        } else {
            return !hasRequiredPermissions(user, [CC_PERMISSIONS.AllNotifications, PERM_TYPES.Read]);
        }
    }
    return !allNotificationsView;
};

const _calcOnlyUserNotificationsVariable = ({
    user,
    allNotificationsView,
    permissionsFF
}: {
    user: ICCUser;
    allNotificationsView?: boolean;
    permissionsFF?: boolean;
}) => {
    if (allNotificationsView === undefined) {
        if (!permissionsFF) {
            return user.role?.displayName !== ROLES.ADMIN;
        } else {
            return !hasRequiredPermissions(user, [CC_PERMISSIONS.AllNotifications, PERM_TYPES.Read]);
        }
    }
    return !allNotificationsView;
};

export const useOnlyUserNotificationsVariable = () => {
    const { user, allNotificationsView } = useSelector(
        state => ({
            user: state.common.user,
            allNotificationsView: state.notifications.allNotificationsView
        }),
        shallowEqual
    );
    const permissionsFF = useFeatureFlag(FeatureFlagKeys.Permissions);

    return _calcOnlyUserNotificationsVariable({ user, allNotificationsView, permissionsFF });
};

const useVariables = (overrideOnlyUserNotifications?: boolean) => {
    const { systemId, environmentId } = useSelector(
        state => ({
            systemId: state.common.currentSystem,
            environmentId: state.common.currentEnvironment?.id
        }),
        shallowEqual
    );

    const onlyUserNotifications = useOnlyUserNotificationsVariable();

    return {
        systemId,
        environmentId,
        onlyUserNotifications: overrideOnlyUserNotifications ?? onlyUserNotifications
    };
};

export const getNotificationDataVariables = ({
    environmentId,
    systemId,
    tableState,
    searchTerm,
    onlyUserNotifications
}: {
    environmentId: string;
    systemId: string;
    tableState: ITableData;
    searchTerm?: string;
    onlyUserNotifications: boolean;
}) => {
    return {
        systemId,
        environmentId,
        onlyUserNotifications,
        query: {
            sorting: tableState.sorting as GetNotificationsArgsSort,
            filters: Object.entries(tableState.filters).filter(([, values]) => values.length > 0),
            searchTerm,
            page: tableState.page,
            limit: tableState.limit
        }
    };
};

export const useNotificationsData = (key: TableKeys): { data?: _NotificationsListDataFragment | null; loading: boolean } => {
    const [data, setData] = useState<_NotificationsListDataFragment | null>(null);
    const { cache } = useApolloClient();
    const { useQuery } = notificationsQueries[key];

    const onlyUserNotifications = useOnlyUserNotificationsVariable();
    const _skip = useSkip();

    const { activeTab, environmentId, systemId, tableStates, searchTerm } = useSelector(
        state => ({
            activeTab: state.notifications?.activeTab,
            environmentId: state.common?.currentEnvironment?.id,
            systemId: state.common?.currentSystem,
            tableStates: state.notifications.tableStates,
            searchTerm: state.notifications.searchTerm
        }),
        shallowEqual
    );

    const activeTable = TabsConfig[activeTab].tableKey;

    const waitForActiveTab =
        activeTable !== key &&
        !cache.readQuery({
            query: notificationsQueries[activeTable].doc,
            variables: getNotificationDataVariables({
                environmentId,
                systemId,
                tableState: tableStates[activeTable],
                searchTerm,
                onlyUserNotifications
            })
        });

    const { loading } = useQuery({
        skip: _skip || waitForActiveTab,
        notifyOnNetworkStatusChange: true,
        onCompleted: _data => setData(_data.notifications),
        variables: getNotificationDataVariables({
            environmentId,
            systemId,
            tableState: tableStates[key],
            searchTerm,
            onlyUserNotifications
        })
    });

    return { data, loading };
};

export const useNotificationsAverageReceived = () => {
    const skip = useSkip();
    const variables = useVariables();
    const { data } = useGetNotificationsAverageReceivedQuery({ skip, variables });
    return data?.getNotificationsAverageReceived;
};

export const useNotificationsCount = (overrideOnlyUserNotifications?: boolean) => {
    const skip = useSkip();
    const variables = useVariables(overrideOnlyUserNotifications);
    const { data } = useGetNotificationsCountQuery({ skip, variables });
    return data?.getNotificationsCount;
};

export const useNotificationsFilterFields = (overrideOnlyUserNotifications?: boolean) => {
    const skip = useSkip();
    const variables = useVariables(overrideOnlyUserNotifications);
    const { data } = useGetNotificationsFiltersFieldsQuery({ skip, variables });
    return data?.getNotificationsFiltersFields;
};

export const useNotificationsIsEmptyState = (overrideOnlyUserNotifications?: boolean) => {
    const notificationsCount = useNotificationsCount(overrideOnlyUserNotifications);

    return useMemo(() => !!notificationsCount && Object.values(TableKeys).every(key => notificationsCount[key] === 0), [notificationsCount]);
};

export const useGetNotificationById = (notificationId: $TSFixMe) => {
    const { systemId, environmentId } = useSelector(
        state => ({
            systemId: state.common.currentSystem,
            environmentId: state.common.currentEnvironment?.id
        }),
        shallowEqual
    );

    const { data, ...rest } = useGetNotificationByIdQuery({
        variables: { systemId, environmentId, notificationId },
        skip: !systemId || !environmentId || !notificationId
    });

    const notification = data?.getNotificationById as INotification;

    return { notification, ...rest };
};
